import { ClipVertex, b2Point } from "./collision.js";
import { b2Math, b2Vec2, b2XForm } from "./common/math.js";
import { b2Settings } from "./common/settings.js";
import { b2CircleShape, b2PolygonShape, b2Shape } from "./shapes.js";

export class b2Collision {
  static b2_nullFeature = 255;
  static b2CollidePolyTempVec = new b2Vec2();

  static EdgeSeparation(poly1, xf1, edge1, poly2, xf2) {
    const count1 = poly1.m_vertexCount;
    const vertices1 = poly1.m_vertices;
    const normals1 = poly1.m_normals;
    const count2 = poly2.m_vertexCount;
    const vertices2 = poly2.m_vertices;
    let matrix = xf1.R;
    let normal = normals1[edge1];
    const normal1WorldX = matrix.col1.x * normal.x + matrix.col2.x * normal.y;
    const normal1WorldY = matrix.col1.y * normal.x + matrix.col2.y * normal.y;
    matrix = xf2.R;
    const normal1X = matrix.col1.x * normal1WorldX + matrix.col1.y * normal1WorldY;
    const normal1Y = matrix.col2.x * normal1WorldX + matrix.col2.y * normal1WorldY;
    let index = 0;
    let minDot = Number.MAX_VALUE;
    for (let i = 0; i < count2; i += 1) {
      normal = vertices2[i];
      const dot = normal.x * normal1X + normal.y * normal1Y;
      if (dot < minDot) {
        minDot = dot;
        index = i;
      }
    }
    normal = vertices1[edge1];
    matrix = xf1.R;
    const v1X = xf1.position.x + (matrix.col1.x * normal.x + matrix.col2.x * normal.y);
    const v1Y = xf1.position.y + (matrix.col1.y * normal.x + matrix.col2.y * normal.y);
    normal = vertices2[index];
    matrix = xf2.R;
    let v2X = xf2.position.x + (matrix.col1.x * normal.x + matrix.col2.x * normal.y);
    let v2Y = xf2.position.y + (matrix.col1.y * normal.x + matrix.col2.y * normal.y);
    v2X -= v1X;
    v2Y -= v1Y;
    return v2X * normal1WorldX + v2Y * normal1WorldY;
  }

  static b2TestOverlap(aabb1, aabb2) {
    let dX = aabb2.lowerBound.x - aabb1.upperBound.x;
    let dY = aabb2.lowerBound.y - aabb1.upperBound.y;
    if (dX > 0 || dY > 0) {
      return false;
    }
    dX = aabb1.lowerBound.x - aabb2.upperBound.x;
    dY = aabb1.lowerBound.y - aabb2.upperBound.y;
    if (dX > 0 || dY > 0) {
      return false;
    }
    return true;
  }

  static FindIncidentEdge(c, poly1, xf1, edge1, poly2, xf2) {
    const normals1 = poly1.m_normals;
    const count2 = poly2.m_vertexCount;
    const vertices2 = poly2.m_vertices;
    const normals2 = poly2.m_normals;
    let matrix = xf1.R;
    let normal = normals1[edge1];
    let normal1X = matrix.col1.x * normal.x + matrix.col2.x * normal.y;
    let normal1Y = matrix.col1.y * normal.x + matrix.col2.y * normal.y;
    matrix = xf2.R;
    const tX = matrix.col1.x * normal1X + matrix.col1.y * normal1Y;
    normal1Y = matrix.col2.x * normal1X + matrix.col2.y * normal1Y;
    normal1X = tX;
    let index = 0;
    let minDot = Number.MAX_VALUE;
    for (let i = 0; i < count2; i += 1) {
      normal = normals2[i];
      const dot = normal1X * normal.x + normal1Y * normal.y;
      if (dot < minDot) {
        minDot = dot;
        index = i;
      }
    }
    const i1 = index;
    const i2 = i1 + 1 < count2 ? i1 + 1 : 0;
    let clipVertex = c[0];
    normal = vertices2[i1];
    matrix = xf2.R;
    clipVertex.v.x = xf2.position.x + (matrix.col1.x * normal.x + matrix.col2.x * normal.y);
    clipVertex.v.y = xf2.position.y + (matrix.col1.y * normal.x + matrix.col2.y * normal.y);
    clipVertex.id.features.referenceEdge = edge1;
    clipVertex.id.features.incidentEdge = i1;
    clipVertex.id.features.incidentVertex = 0;
    clipVertex = c[1];
    normal = vertices2[i2];
    matrix = xf2.R;
    clipVertex.v.x = xf2.position.x + (matrix.col1.x * normal.x + matrix.col2.x * normal.y);
    clipVertex.v.y = xf2.position.y + (matrix.col1.y * normal.x + matrix.col2.y * normal.y);
    clipVertex.id.features.referenceEdge = edge1;
    clipVertex.id.features.incidentEdge = i2;
    clipVertex.id.features.incidentVertex = 1;
  }

  static b2CollidePolygons(manifold, polyA, xfA, polyB, xfB) {
    manifold.pointCount = 0;
    manifold.m_pointCount = 0;
    const edgeAOut = [0];
    const separationA = b2Collision.FindMaxSeparation(edgeAOut, polyA, xfA, polyB, xfB);
    const edgeA = edgeAOut[0];
    if (separationA > 0) {
      return;
    }
    const edgeBOut = [0];
    const separationB = b2Collision.FindMaxSeparation(edgeBOut, polyB, xfB, polyA, xfA);
    const edgeB = edgeBOut[0];
    if (separationB > 0) {
      return;
    }
    let poly1;
    let poly2;
    const xf1 = new b2XForm();
    const xf2 = new b2XForm();
    let edge1 = 0;
    let flip = 0;
    const relativeTol = 0.98;
    const absoluteTol = 0.001;
    if (separationB > relativeTol * separationA + absoluteTol) {
      poly1 = polyB;
      poly2 = polyA;
      xf1.Set(xfB);
      xf2.Set(xfA);
      edge1 = edgeB;
      flip = 1;
    } else {
      poly1 = polyA;
      poly2 = polyB;
      xf1.Set(xfA);
      xf2.Set(xfB);
      edge1 = edgeA;
      flip = 0;
    }
    const incidentEdge = [new ClipVertex(), new ClipVertex()];
    b2Collision.FindIncidentEdge(incidentEdge, poly1, xf1, edge1, poly2, xf2);
    const count1 = poly1.m_vertexCount;
    const vertices1 = poly1.m_vertices;
    let localV11 = vertices1[edge1].Copy();
    let localV12;
    if (edge1 + 1 < count1) {
      localV12 = vertices1[edge1 + 1].Copy();
    } else {
      localV12 = vertices1[0].Copy();
    }
    let sideNormal = b2Math.b2MulMV(xf1.R, b2Math.SubtractVV(localV12, localV11));
    sideNormal.Normalize();
    const frontNormal = b2Math.b2CrossVF(sideNormal, 1);
    localV11 = b2Math.b2MulX(xf1, localV11);
    localV12 = b2Math.b2MulX(xf1, localV12);
    const frontOffset = b2Math.b2Dot(frontNormal, localV11);
    const sideOffset1 = -b2Math.b2Dot(sideNormal, localV11);
    const sideOffset2 = b2Math.b2Dot(sideNormal, localV12);
    const clipPoints1 = [new ClipVertex(), new ClipVertex()];
    const clipPoints2 = [new ClipVertex(), new ClipVertex()];
    let np = b2Collision.ClipSegmentToLine(clipPoints1, incidentEdge, sideNormal.Negative(), sideOffset1);
    if (np < 2) {
      return;
    }
    np = b2Collision.ClipSegmentToLine(clipPoints2, clipPoints1, sideNormal, sideOffset2);
    if (np < 2) {
      return;
    }
    manifold.normal.SetV(flip ? frontNormal.Negative() : frontNormal);
    manifold.m_localPlaneNormal.SetV(manifold.normal);
    manifold.m_type = flip ? manifold.constructor.e_faceB : manifold.constructor.e_faceA;
    let pointCount = 0;
    for (let i = 0; i < b2Settings.b2_maxManifoldPoints; i += 1) {
      const clipPoint = clipPoints2[i];
      const separation = b2Math.b2Dot(frontNormal, clipPoint.v) - frontOffset;
      if (separation <= 0) {
        const cp = manifold.points[pointCount];
        cp.separation = separation;
        cp.localPoint1.SetV(b2Math.b2MulXT(xfA, clipPoint.v));
        cp.localPoint2.SetV(b2Math.b2MulXT(xfB, clipPoint.v));
        cp.id.key = clipPoint.id._key;
        cp.id.features.flip = flip;
        pointCount += 1;
      }
    }
    manifold.pointCount = pointCount;
    manifold.m_pointCount = pointCount;
  }

  static FindMaxSeparation(edgeIndex, poly1, xf1, poly2, xf2) {
    const count1 = poly1.m_vertexCount;
    const normals1 = poly1.m_normals;
    let matrix = xf2.R;
    let vertex = poly2.m_centroid;
    let dX = xf2.position.x + (matrix.col1.x * vertex.x + matrix.col2.x * vertex.y);
    let dY = xf2.position.y + (matrix.col1.y * vertex.x + matrix.col2.y * vertex.y);
    matrix = xf1.R;
    vertex = poly1.m_centroid;
    dX -= xf1.position.x + (matrix.col1.x * vertex.x + matrix.col2.x * vertex.y);
    dY -= xf1.position.y + (matrix.col1.y * vertex.x + matrix.col2.y * vertex.y);
    const dLocal1X = dX * xf1.R.col1.x + dY * xf1.R.col1.y;
    const dLocal1Y = dX * xf1.R.col2.x + dY * xf1.R.col2.y;
    let edge = 0;
    let maxDot = -Number.MAX_VALUE;
    for (let i = 0; i < count1; i += 1) {
      vertex = normals1[i];
      const dot = vertex.x * dLocal1X + vertex.y * dLocal1Y;
      if (dot > maxDot) {
        maxDot = dot;
        edge = i;
      }
    }
    let s = b2Collision.EdgeSeparation(poly1, xf1, edge, poly2, xf2);
    if (s > 0) {
      return s;
    }
    const prevEdge = edge - 1 >= 0 ? edge - 1 : count1 - 1;
    const sPrev = b2Collision.EdgeSeparation(poly1, xf1, prevEdge, poly2, xf2);
    if (sPrev > 0) {
      return sPrev;
    }
    const nextEdge = edge + 1 < count1 ? edge + 1 : 0;
    const sNext = b2Collision.EdgeSeparation(poly1, xf1, nextEdge, poly2, xf2);
    if (sNext > 0) {
      return sNext;
    }
    let bestEdge;
    let increment;
    let bestSeparation;
    if (sPrev > s && sPrev > sNext) {
      increment = -1;
      bestEdge = prevEdge;
      bestSeparation = sPrev;
    } else {
      if (sNext <= s) {
        edgeIndex[0] = edge;
        return s;
      }
      increment = 1;
      bestEdge = nextEdge;
      bestSeparation = sNext;
    }
    while (true) {
      if (increment === -1) {
        edge = bestEdge - 1 >= 0 ? bestEdge - 1 : count1 - 1;
      } else {
        edge = bestEdge + 1 < count1 ? bestEdge + 1 : 0;
      }
      s = b2Collision.EdgeSeparation(poly1, xf1, edge, poly2, xf2);
      if (s > 0) {
        break;
      }
      if (s <= bestSeparation) {
        edgeIndex[0] = bestEdge;
        return bestSeparation;
      }
      bestEdge = edge;
      bestSeparation = s;
    }
    return s;
  }

  static ClipSegmentToLine(vOut, vIn, normal, offset) {
    let numOut = 0;
    const vIn0 = vIn[0].v;
    const vIn1 = vIn[1].v;
    const distance0 = b2Math.b2Dot(normal, vIn0) - offset;
    const distance1 = b2Math.b2Dot(normal, vIn1) - offset;
    if (distance0 <= 0) {
      vOut[numOut++] = vIn[0];
    }
    if (distance1 <= 0) {
      vOut[numOut++] = vIn[1];
    }
    if (distance0 * distance1 < 0) {
      const interp = distance0 / (distance0 - distance1);
      const clipVertex = vOut[numOut];
      const v = clipVertex.v;
      v.x = vIn0.x + interp * (vIn1.x - vIn0.x);
      v.y = vIn0.y + interp * (vIn1.y - vIn0.y);
      if (distance0 > 0) {
        clipVertex.id = vIn[0].id;
      } else {
        clipVertex.id = vIn[1].id;
      }
      numOut += 1;
    }
    return numOut;
  }

  static b2CollideCircles(manifold, circle1, xf1, circle2, xf2) {
    manifold.pointCount = 0;
    manifold.m_pointCount = 0;
    let matrix = xf1.R;
    let point = circle1.m_localPosition;
    let p1X = xf1.position.x + (matrix.col1.x * point.x + matrix.col2.x * point.y);
    let p1Y = xf1.position.y + (matrix.col1.y * point.x + matrix.col2.y * point.y);
    matrix = xf2.R;
    point = circle2.m_localPosition;
    let p2X = xf2.position.x + (matrix.col1.x * point.x + matrix.col2.x * point.y);
    let p2Y = xf2.position.y + (matrix.col1.y * point.x + matrix.col2.y * point.y);
    const dX = p2X - p1X;
    const dY = p2Y - p1Y;
    const distSqr = dX * dX + dY * dY;
    const r1 = circle1.m_radius;
    const r2 = circle2.m_radius;
    const radius = r1 + r2;
    if (distSqr > radius * radius) {
      return;
    }
    let separation;
    if (distSqr < Number.MIN_VALUE) {
      separation = -radius;
      manifold.normal.Set(0, 1);
      manifold.m_localPlaneNormal.SetV(manifold.normal);
    } else {
      const dist = Math.sqrt(distSqr);
      separation = dist - radius;
      const a = 1 / dist;
      manifold.normal.x = a * dX;
      manifold.normal.y = a * dY;
      manifold.m_localPlaneNormal.SetV(manifold.normal);
    }
    manifold.pointCount = 1;
    manifold.m_pointCount = 1;
    manifold.m_type = manifold.constructor.e_circles;
    manifold.m_localPoint.SetV(circle1.m_localPosition ?? circle1.m_p);
    const point0 = manifold.points[0];
    point0.id.key = 0;
    point0.separation = separation;
    p1X += r1 * manifold.normal.x;
    p1Y += r1 * manifold.normal.y;
    p2X -= r2 * manifold.normal.x;
    p2Y -= r2 * manifold.normal.y;
    const pX = 0.5 * (p1X + p2X);
    const pY = 0.5 * (p1Y + p2Y);
    let tX = pX - xf1.position.x;
    let tY = pY - xf1.position.y;
    point0.localPoint1.x = tX * xf1.R.col1.x + tY * xf1.R.col1.y;
    point0.localPoint1.y = tX * xf1.R.col2.x + tY * xf1.R.col2.y;
    tX = pX - xf2.position.x;
    tY = pY - xf2.position.y;
    point0.localPoint2.x = tX * xf2.R.col1.x + tY * xf2.R.col1.y;
    point0.localPoint2.y = tX * xf2.R.col2.x + tY * xf2.R.col2.y;
  }

  static b2CollidePolygonAndCircle(manifold, polygon, xf1, circle, xf2) {
    manifold.pointCount = 0;
    manifold.m_pointCount = 0;
    let matrix = xf2.R;
    let point = circle.m_localPosition;
    const cX = xf2.position.x + (matrix.col1.x * point.x + matrix.col2.x * point.y);
    const cY = xf2.position.y + (matrix.col1.y * point.x + matrix.col2.y * point.y);
    let dX = cX - xf1.position.x;
    let dY = cY - xf1.position.y;
    matrix = xf1.R;
    const cLocalX = dX * matrix.col1.x + dY * matrix.col1.y;
    const cLocalY = dX * matrix.col2.x + dY * matrix.col2.y;
    let normalIndex = 0;
    let separation = -Number.MAX_VALUE;
    const radius = circle.m_radius;
    const vertexCount = polygon.m_vertexCount;
    const vertices = polygon.m_vertices;
    const normals = polygon.m_normals;
    for (let i = 0; i < vertexCount; i += 1) {
      point = vertices[i];
      dX = cLocalX - point.x;
      dY = cLocalY - point.y;
      point = normals[i];
      const s = point.x * dX + point.y * dY;
      if (s > radius) {
        return;
      }
      if (s > separation) {
        separation = s;
        normalIndex = i;
      }
    }
    if (separation < Number.MIN_VALUE) {
      manifold.pointCount = 1;
      manifold.m_pointCount = 1;
      manifold.m_type = manifold.constructor.e_faceA;
      point = normals[normalIndex];
      matrix = xf1.R;
      manifold.normal.x = matrix.col1.x * point.x + matrix.col2.x * point.y;
      manifold.normal.y = matrix.col1.y * point.x + matrix.col2.y * point.y;
      manifold.m_localPlaneNormal.SetV(manifold.normal);
      manifold.m_localPoint.SetV(vertices[normalIndex]);
      const cp = manifold.points[0];
      cp.id.features.incidentEdge = normalIndex;
      cp.id.features.incidentVertex = b2Collision.b2_nullFeature;
      cp.id.features.referenceEdge = 0;
      cp.id.features.flip = 0;
      const pX = cX - radius * manifold.normal.x;
      const pY = cY - radius * manifold.normal.y;
      dX = pX - xf1.position.x;
      dY = pY - xf1.position.y;
      matrix = xf1.R;
      cp.localPoint1.x = dX * matrix.col1.x + dY * matrix.col1.y;
      cp.localPoint1.y = dX * matrix.col2.x + dY * matrix.col2.y;
      dX = pX - xf2.position.x;
      dY = pY - xf2.position.y;
      matrix = xf2.R;
      cp.localPoint2.x = dX * matrix.col1.x + dY * matrix.col1.y;
      cp.localPoint2.y = dX * matrix.col2.x + dY * matrix.col2.y;
      cp.separation = separation - radius;
      return;
    }
    const vertIndex1 = normalIndex;
    const vertIndex2 = vertIndex1 + 1 < vertexCount ? vertIndex1 + 1 : 0;
    const v1 = vertices[vertIndex1];
    const v2 = vertices[vertIndex2];
    let edgeX = v2.x - v1.x;
    let edgeY = v2.y - v1.y;
    const edgeLength = Math.sqrt(edgeX * edgeX + edgeY * edgeY);
    edgeX /= edgeLength;
    edgeY /= edgeLength;
    dX = cLocalX - v1.x;
    dY = cLocalY - v1.y;
    const u = dX * edgeX + dY * edgeY;
    const cp = manifold.points[0];
    let pX;
    let pY;
    if (u <= 0) {
      pX = v1.x;
      pY = v1.y;
      cp.id.features.incidentEdge = b2Collision.b2_nullFeature;
      cp.id.features.incidentVertex = vertIndex1;
    } else if (u >= edgeLength) {
      pX = v2.x;
      pY = v2.y;
      cp.id.features.incidentEdge = b2Collision.b2_nullFeature;
      cp.id.features.incidentVertex = vertIndex2;
    } else {
      pX = edgeX * u + v1.x;
      pY = edgeY * u + v1.y;
      cp.id.features.incidentEdge = normalIndex;
      cp.id.features.incidentVertex = b2Collision.b2_nullFeature;
    }
    dX = cLocalX - pX;
    dY = cLocalY - pY;
    let dist = Math.sqrt(dX * dX + dY * dY);
    dX /= dist;
    dY /= dist;
    if (dist > radius) {
      return;
    }
    manifold.pointCount = 1;
    manifold.m_pointCount = 1;
    manifold.m_type = manifold.constructor.e_faceA;
    matrix = xf1.R;
    manifold.normal.x = matrix.col1.x * dX + matrix.col2.x * dY;
    manifold.normal.y = matrix.col1.y * dX + matrix.col2.y * dY;
    manifold.m_localPlaneNormal.SetV(manifold.normal);
    manifold.m_localPoint.Set(pX, pY);
    const worldX = cX - radius * manifold.normal.x;
    const worldY = cY - radius * manifold.normal.y;
    dX = worldX - xf1.position.x;
    dY = worldY - xf1.position.y;
    matrix = xf1.R;
    cp.localPoint1.x = dX * matrix.col1.x + dY * matrix.col1.y;
    cp.localPoint1.y = dX * matrix.col2.x + dY * matrix.col2.y;
    dX = worldX - xf2.position.x;
    dY = worldY - xf2.position.y;
    matrix = xf2.R;
    cp.localPoint2.x = dX * matrix.col1.x + dY * matrix.col1.y;
    cp.localPoint2.y = dX * matrix.col2.x + dY * matrix.col2.y;
    cp.separation = dist - radius;
    cp.id.features.referenceEdge = 0;
    cp.id.features.flip = 0;
  }
}

export class b2Distance {
  static s_p1s = [new b2Vec2(), new b2Vec2(), new b2Vec2()];
  static s_p2s = [new b2Vec2(), new b2Vec2(), new b2Vec2()];
  static s_points = [new b2Vec2(), new b2Vec2(), new b2Vec2()];
  static gPoint = new b2Point();
  static g_GJK_Iterations = 0;

  static InPoints(w, points, pointCount) {
    const kTol = 100 * Number.MIN_VALUE;
    for (let i = 0; i < pointCount; i += 1) {
      const point = points[i];
      const dX = Math.abs(w.x - point.x);
      const dY = Math.abs(w.y - point.y);
      const mX = Math.max(Math.abs(w.x), Math.abs(point.x));
      const mY = Math.max(Math.abs(w.y), Math.abs(point.y));
      if (dX < kTol * (mX + 1) && dY < kTol * (mY + 1)) {
        return true;
      }
    }
    return false;
  }

  static DistanceGeneric(x1, x2, shape1, xf1, shape2, xf2) {
    const p1s = b2Distance.s_p1s;
    const p2s = b2Distance.s_p2s;
    const points = b2Distance.s_points;
    let pointCount = 0;
    x1.SetV(shape1.GetFirstVertex(xf1));
    x2.SetV(shape2.GetFirstVertex(xf2));
    let vSqr = 0;
    const maxIterations = 20;
    for (let iter = 0; iter < maxIterations; iter += 1) {
      const vX = x2.x - x1.x;
      const vY = x2.y - x1.y;
      const w1 = shape1.Support(xf1, vX, vY);
      const w2 = shape2.Support(xf2, -vX, -vY);
      vSqr = vX * vX + vY * vY;
      const wX = w2.x - w1.x;
      const wY = w2.y - w1.y;
      const vw = vX * wX + vY * wY;
      if (vSqr - vw <= 0.01 * vSqr) {
        if (pointCount === 0) {
          x1.SetV(w1);
          x2.SetV(w2);
        }
        b2Distance.g_GJK_Iterations = iter;
        return Math.sqrt(vSqr);
      }
      switch (pointCount) {
        case 0:
          p1s[0].SetV(w1);
          p2s[0].SetV(w2);
          points[0].x = wX;
          points[0].y = wY;
          x1.SetV(p1s[0]);
          x2.SetV(p2s[0]);
          pointCount += 1;
          break;
        case 1:
          p1s[1].SetV(w1);
          p2s[1].SetV(w2);
          points[1].x = wX;
          points[1].y = wY;
          pointCount = b2Distance.ProcessTwo(x1, x2, p1s, p2s, points);
          break;
        case 2:
          p1s[2].SetV(w1);
          p2s[2].SetV(w2);
          points[2].x = wX;
          points[2].y = wY;
          pointCount = b2Distance.ProcessThree(x1, x2, p1s, p2s, points);
          break;
      }
      if (pointCount === 3) {
        b2Distance.g_GJK_Iterations = iter;
        return 0;
      }
      let maxSqr = -Number.MAX_VALUE;
      for (let i = 0; i < pointCount; i += 1) {
        const point = points[i];
        maxSqr = b2Math.b2Max(maxSqr, point.x * point.x + point.y * point.y);
      }
      if (pointCount === 3 || vSqr <= 100 * Number.MIN_VALUE * maxSqr) {
        b2Distance.g_GJK_Iterations = iter;
        const dX = x2.x - x1.x;
        const dY = x2.y - x1.y;
        vSqr = dX * dX + dY * dY;
        return Math.sqrt(vSqr);
      }
    }
    b2Distance.g_GJK_Iterations = maxIterations;
    return Math.sqrt(vSqr);
  }

  static DistanceCC(x1, x2, circle1, xf1, circle2, xf2) {
    let matrix = xf1.R;
    let point = circle1.m_localPosition;
    const p1X = xf1.position.x + (matrix.col1.x * point.x + matrix.col2.x * point.y);
    const p1Y = xf1.position.y + (matrix.col1.y * point.x + matrix.col2.y * point.y);
    matrix = xf2.R;
    point = circle2.m_localPosition;
    const p2X = xf2.position.x + (matrix.col1.x * point.x + matrix.col2.x * point.y);
    const p2Y = xf2.position.y + (matrix.col1.y * point.x + matrix.col2.y * point.y);
    let dX = p2X - p1X;
    let dY = p2Y - p1Y;
    const dSqr = dX * dX + dY * dY;
    const r1 = circle1.m_radius - b2Settings.b2_toiSlop;
    const r2 = circle2.m_radius - b2Settings.b2_toiSlop;
    const r = r1 + r2;
    if (dSqr > r * r) {
      const d = Math.sqrt(dSqr);
      dX /= d;
      dY /= d;
      const distance = d - r;
      x1.x = p1X + r1 * dX;
      x1.y = p1Y + r1 * dY;
      x2.x = p2X - r2 * dX;
      x2.y = p2Y - r2 * dY;
      return distance;
    }
    if (dSqr > Number.MIN_VALUE * Number.MIN_VALUE) {
      const d = Math.sqrt(dSqr);
      dX /= d;
      dY /= d;
      x1.x = p1X + r1 * dX;
      x1.y = p1Y + r1 * dY;
      x2.x = x1.x;
      x2.y = x1.y;
      return 0;
    }
    x1.x = p1X;
    x1.y = p1Y;
    x2.x = x1.x;
    x2.y = x1.y;
    return 0;
  }

  static ProcessThree(x1, x2, p1s, p2s, points) {
    const a = points[0];
    const b = points[1];
    const c = points[2];
    const p1sA = p1s[0];
    const p1sB = p1s[1];
    const p1sC = p1s[2];
    const p2sA = p2s[0];
    const p2sB = p2s[1];
    const p2sC = p2s[2];
    const abX = b.x - a.x;
    const abY = b.y - a.y;
    const acX = c.x - a.x;
    const acY = c.y - a.y;
    const bcX = c.x - b.x;
    const bcY = c.y - b.y;
    const sn = -(a.x * abX + a.y * abY);
    const sd = b.x * abX + b.y * abY;
    const tn = -(a.x * acX + a.y * acY);
    const td = c.x * acX + c.y * acY;
    const un = -(b.x * bcX + b.y * bcY);
    const ud = c.x * bcX + c.y * bcY;
    if (td <= 0 && ud <= 0) {
      x1.SetV(p1sC);
      x2.SetV(p2sC);
      p1sA.SetV(p1sC);
      p2sA.SetV(p2sC);
      a.SetV(c);
      return 1;
    }
    const n = abX * acY - abY * acX;
    const vc = n * (a.x * b.y - a.y * b.x);
    const va = n * (b.x * c.y - b.y * c.x);
    if (va <= 0 && un >= 0 && ud >= 0 && un + ud > 0) {
      const lambda = un / (un + ud);
      x1.x = p1sB.x + lambda * (p1sC.x - p1sB.x);
      x1.y = p1sB.y + lambda * (p1sC.y - p1sB.y);
      x2.x = p2sB.x + lambda * (p2sC.x - p2sB.x);
      x2.y = p2sB.y + lambda * (p2sC.y - p2sB.y);
      p1sA.SetV(p1sC);
      p2sA.SetV(p2sC);
      a.SetV(c);
      return 2;
    }
    const vb = n * (c.x * a.y - c.y * a.x);
    if (vb <= 0 && tn >= 0 && td >= 0 && tn + td > 0) {
      const lambda = tn / (tn + td);
      x1.x = p1sA.x + lambda * (p1sC.x - p1sA.x);
      x1.y = p1sA.y + lambda * (p1sC.y - p1sA.y);
      x2.x = p2sA.x + lambda * (p2sC.x - p2sA.x);
      x2.y = p2sA.y + lambda * (p2sC.y - p2sA.y);
      p1sB.SetV(p1sC);
      p2sB.SetV(p2sC);
      b.SetV(c);
      return 2;
    }
    const denom = 1 / (va + vb + vc);
    const u = va * denom;
    const v = vb * denom;
    const w = 1 - u - v;
    x1.x = u * p1sA.x + v * p1sB.x + w * p1sC.x;
    x1.y = u * p1sA.y + v * p1sB.y + w * p1sC.y;
    x2.x = u * p2sA.x + v * p2sB.x + w * p2sC.x;
    x2.y = u * p2sA.y + v * p2sB.y + w * p2sC.y;
    return 3;
  }

  static DistancePC(x1, x2, polygon, xf1, circle, xf2) {
    const point = b2Distance.gPoint;
    const position = circle.m_localPosition;
    const rotation = xf2.R;
    point.p.x = xf2.position.x + (rotation.col1.x * position.x + rotation.col2.x * position.y);
    point.p.y = xf2.position.y + (rotation.col1.y * position.x + rotation.col2.y * position.y);
    let distance = b2Distance.DistanceGeneric(x1, x2, polygon, xf1, point, b2Math.b2XForm_identity);
    const r = circle.m_radius - b2Settings.b2_toiSlop;
    if (distance > r) {
      distance -= r;
      let dX = x2.x - x1.x;
      let dY = x2.y - x1.y;
      const dLen = Math.sqrt(dX * dX + dY * dY);
      dX /= dLen;
      dY /= dLen;
      x2.x -= r * dX;
      x2.y -= r * dY;
    } else {
      distance = 0;
      x2.x = x1.x;
      x2.y = x1.y;
    }
    return distance;
  }

  static Distance(x1, x2, shape1, xf1, shape2, xf2) {
    const type1 = shape1.m_type;
    const type2 = shape2.m_type;
    if (type1 === b2Shape.e_circleShape && type2 === b2Shape.e_circleShape) {
      return b2Distance.DistanceCC(x1, x2, shape1, xf1, shape2, xf2);
    }
    if (type1 === b2Shape.e_polygonShape && type2 === b2Shape.e_circleShape) {
      return b2Distance.DistancePC(x1, x2, shape1, xf1, shape2, xf2);
    }
    if (type1 === b2Shape.e_circleShape && type2 === b2Shape.e_polygonShape) {
      return b2Distance.DistancePC(x2, x1, shape2, xf2, shape1, xf1);
    }
    if (type1 === b2Shape.e_polygonShape && type2 === b2Shape.e_polygonShape) {
      return b2Distance.DistanceGeneric(x1, x2, shape1, xf1, shape2, xf2);
    }
    return 0;
  }

  static ProcessTwo(x1, x2, p1s, p2s, points) {
    const a = points[0];
    const b = points[1];
    const p1sA = p1s[0];
    const p1sB = p1s[1];
    const p2sA = p2s[0];
    const p2sB = p2s[1];
    const dX = -b.x;
    const dY = -b.y;
    let eX = a.x - b.x;
    let eY = a.y - b.y;
    const length = Math.sqrt(eX * eX + eY * eY);
    eX /= length;
    eY /= length;
    let lambda = dX * eX + dY * eY;
    if (lambda <= 0 || length < Number.MIN_VALUE) {
      x1.SetV(p1sB);
      x2.SetV(p2sB);
      p1sA.SetV(p1sB);
      p2sA.SetV(p2sB);
      a.SetV(b);
      return 1;
    }
    lambda /= length;
    x1.x = p1sB.x + lambda * (p1sA.x - p1sB.x);
    x1.y = p1sB.y + lambda * (p1sA.y - p1sB.y);
    x2.x = p2sB.x + lambda * (p2sA.x - p2sB.x);
    x2.y = p2sB.y + lambda * (p2sA.y - p2sB.y);
    return 2;
  }
}

export class b2TimeOfImpact {
  static s_p1 = new b2Vec2();
  static s_p2 = new b2Vec2();
  static s_xf1 = new b2XForm();
  static s_xf2 = new b2XForm();

  static TimeOfImpact(shape1, sweep1, shape2, sweep2) {
    const sweepRadius1 = shape1.m_sweepRadius;
    const sweepRadius2 = shape2.m_sweepRadius;
    const t0 = sweep1.t0;
    const delta1X = sweep1.c.x - sweep1.c0.x;
    const delta1Y = sweep1.c.y - sweep1.c0.y;
    const delta2X = sweep2.c.x - sweep2.c0.x;
    const delta2Y = sweep2.c.y - sweep2.c0.y;
    const angular1 = sweep1.a - sweep1.a0;
    const angular2 = sweep2.a - sweep2.a0;
    let alpha = 0;
    const p1 = b2TimeOfImpact.s_p1;
    const p2 = b2TimeOfImpact.s_p2;
    const maxIterations = 20;
    let iter = 0;
    let normalX = 0;
    let normalY = 0;
    let distance = 0;
    let targetDistance = 0;
    while (true) {
      const time = (1 - alpha) * t0 + alpha;
      const xf1 = b2TimeOfImpact.s_xf1;
      const xf2 = b2TimeOfImpact.s_xf2;
      sweep1.GetXForm(xf1, time);
      sweep2.GetXForm(xf2, time);
      distance = b2Distance.Distance(p1, p2, shape1, xf1, shape2, xf2);
      if (iter === 0) {
        if (distance > 2 * b2Settings.b2_toiSlop) {
          targetDistance = 1.5 * b2Settings.b2_toiSlop;
        } else {
          const t1 = 0.05 * b2Settings.b2_toiSlop;
          const t2 = distance - 0.5 * b2Settings.b2_toiSlop;
          targetDistance = t1 > t2 ? t1 : t2;
        }
      }
      if (distance - targetDistance < 0.05 * b2Settings.b2_toiSlop || iter === maxIterations) {
        break;
      }
      normalX = p2.x - p1.x;
      normalY = p2.y - p1.y;
      const normalLen = Math.sqrt(normalX * normalX + normalY * normalY);
      normalX /= normalLen;
      normalY /= normalLen;
      const approach = normalX * (delta1X - delta2X)
        + normalY * (delta1Y - delta2Y)
        + (angular1 < 0 ? -angular1 : angular1) * sweepRadius1
        + (angular2 < 0 ? -angular2 : angular2) * sweepRadius2;
      if (approach === 0) {
        alpha = 1;
        break;
      }
      const dAlpha = (distance - targetDistance) / approach;
      const nextAlpha = alpha + dAlpha;
      if (nextAlpha < 0 || 1 < nextAlpha) {
        alpha = 1;
        break;
      }
      if (nextAlpha < (1 + 100 * Number.MIN_VALUE) * alpha) {
        break;
      }
      alpha = nextAlpha;
      iter += 1;
    }
    return alpha;
  }
}
